import { Signal } from '@angular/core';
import {
    DatiqGraphCardChartDataSchema,
    DatiqGraphCardChartSchema,
} from '@dat/datiq-components-pkg/graph-card';
import {
    DatiqGeoSuggestionSchema,
    emptyDatiqGeoSuggestion,
} from '@dat/iq-proxy';
import { z } from 'zod';

export const ODGeoSearchPairSchema = z.object({
    origin: z.string(),
    destination: z.string(),
});

const equipmentTypes = ['VAN', 'FLATBED', 'REEFER'] as const;

export const equipmentTypeSchema = z.enum(equipmentTypes);

export const fuelSchema = z.boolean();

export const ODPairWithEquipmentSchema = z.object({
    odGeoSearchPair: ODGeoSearchPairSchema,
    equipment: equipmentTypeSchema,
});

export const UiSpotRateSchema = z.object({
    label: z.string(),
    id: z.string(),
    subLabel: z.string(),
    strong: z.boolean().optional(),
});
export const UiLaneSchema = z.object({
    id: z.string(),
    origin: DatiqGeoSuggestionSchema,
    destination: DatiqGeoSuggestionSchema,
});
export const UiGraphCardSchema = z.object({
    chart: DatiqGraphCardChartSchema,
});

export const GraphQlRateTripSchema = z.object({
    value: z.number(),
});

export const GraphQlRateSchema = z.object({
    perTrip: GraphQlRateTripSchema,
});

export const GraphQlSpotRateSchema = z.object({
    rate: z.object({
        average: GraphQlRateSchema,
        high: GraphQlRateSchema,
        low: GraphQlRateSchema,
    }),
});

export const GraphQlForecastSchema = z.object({
    forecast: z.object({
        rates: z.object({
            trip: z.array(z.object({ average: z.number() })),
            mile: z.array(z.object({ average: z.number() })),
        }),
    }),
});
export const LoadStateSchema = z.object({
    loaded: z.boolean(),
    hasErrors: z.boolean(),
    data: z.unknown().optional(),
});

export type LoadState = z.infer<typeof LoadStateSchema>;

export type GraphQlSpotRate = z.infer<typeof GraphQlSpotRateSchema>;
export type GraphQlForecast = z.infer<typeof GraphQlForecastSchema>;
export type ODGeoSearchPair = z.infer<typeof ODGeoSearchPairSchema>;
export type EquipmentTypes = z.infer<typeof equipmentTypeSchema>;
export type Fuel = z.infer<typeof fuelSchema>;
export type ODGeoSearchPairWithEquipment = z.infer<
    typeof ODPairWithEquipmentSchema
>;

export type UiSpotRate = z.infer<typeof UiSpotRateSchema>;
export type UiLane = z.infer<typeof UiLaneSchema>;
export type UiGraphCard = any;
export const emptyUiSpotRate: UiSpotRate = {
    label: '$--',
    subLabel: '($-- - $--)',
    strong: true,
};
export const emptyUiLane: UiLane = {
    id: '',
    origin: {
        ...emptyDatiqGeoSuggestion,
        displayName: '--',
    },
    destination: {
        ...emptyDatiqGeoSuggestion,
        displayName: '--',
    },
};
export const emptyUiGraphCard: UiGraphCard = {
    chart: {
        data: {
            actual: [],
        },
        current: 0,
    },
};

export interface IIqRateLaneTableLanesStateService {
    getRow(ODPairs: ODGeoSearchPair, equipment: EquipmentTypes): Signal<any>;

    getLane(ODPairs: ODGeoSearchPair): Signal<UiLane>;

    getSpotRate(
        ODPairs: ODGeoSearchPair,
        equipment: EquipmentTypes,
    ): Signal<UiSpotRate>;

    getForecast52WeeksGraph(
        ODPairs: ODGeoSearchPair,
        equipment: EquipmentTypes,
    ): Signal<any>;
}

export interface IRowClick {
    value: {
        origin: string,
        destination: string,
        equipment: string
    }
}

export const vanLanes: ODGeoSearchPair[] = [
    { origin: 'Atlanta, GA', destination: 'Philidelphia, PA' },
    { origin: 'Chicago, IL', destination: 'Atlanta, GA' },
    { origin: 'Elizabeth, NJ', destination: 'Atlanta, GA' },
    { origin: 'Laredo, TX', destination: 'Dallas, TX' },
    { origin: 'Los Angeles, CA', destination: 'Phoenix, AZ' },
];

export const flatbedLanes: ODGeoSearchPair[] = [
    { origin: 'Atlanta, GA', destination: 'Lakeland, FL' },
    { origin: 'Ft Worth, TX', destination: 'Houston, TX' },
    { origin: 'Houston, TX', destination: 'El Paso, TX' },
    { origin: 'Houston, TX', destination: 'Ft Worth, TX' },
    { origin: 'Dallas, TX', destination: 'Lubbock, TX' },
];

export const reeferLanes: ODGeoSearchPair[] = [
    { origin: 'Atlanta, GA', destination: 'Lakeland, FL' },
    { origin: 'Atlanta, GA', destination: 'Miami, FL' },
    { origin: 'Fresno, CA', destination: 'Seattle, WA' },
    { origin: 'Fresno, CA', destination: 'Phoenix, AZ' },
    { origin: 'Laredo, TX', destination: 'Dallas, TX' },
];
